import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

import Container from '../../../../components/Container';
import Loader from '../../../../components/Loader';
import AlertMsg from '../../../../components/AlertMsg';
import AppDialog from '../../../../components/AppDialog';
import RestaurantHeader from '../../../../components/RestaurantHeader';
import Page from '../../../../components/Page';

import { DiscountList } from '../components';
import { useGetDiscounts, useDeleteDiscount, useUpdateDiscount } from '../services';
import { TDiscount } from '../types';

const DiscountsPage = () => {
  const { rest_id: restaurantId } = useParams<{ rest_id: string }>();
  const navigate = useNavigate();

  const [pageNum, setPageNum] = useState(1);
  const pageSize = 10;

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [discountToDelete, setDiscountToDelete] = useState<TDiscount | null>(null);
  const [msgAlert, setMsgAlert] = useState({ open: false, message: '', msgType: 'error' });

  const { data, isLoading, refetch } = useGetDiscounts(restaurantId || '', pageNum, pageSize);
  const discounts = data?.rows || [];
  const totalPages = data?.pages || 0;
  const deleteMutation = useDeleteDiscount(restaurantId || '');
  const updateMutation = useUpdateDiscount(restaurantId || '');

  const handleDeleteClick = (discount: TDiscount) => {
    setDiscountToDelete(discount);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!discountToDelete) return;

    try {
      await deleteMutation.mutateAsync(discountToDelete._id);
      setMsgAlert({ open: true, message: 'Discount disabled successfully', msgType: 'success' });
      setDeleteDialogOpen(false);
      setDiscountToDelete(null);
      refetch();
    } catch {
      setMsgAlert({ open: true, message: 'Failed to disable discount', msgType: 'error' });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDiscountToDelete(null);
  };

  const handleToggleStatus = async (discount: TDiscount) => {
    const newStatus = discount.status === 'active' ? 'disabled' : 'active';
    try {
      await updateMutation.mutateAsync({
        id: discount._id,
        payload: { status: newStatus },
      });
      setMsgAlert({
        open: true,
        message: `Discount ${newStatus === 'active' ? 'activated' : 'disabled'} successfully`,
        msgType: 'success',
      });
      refetch();
    } catch {
      setMsgAlert({ open: true, message: 'Failed to update discount status', msgType: 'error' });
    }
  };

  return (
    <Container page="restaurants" searchVal="" onSearch={() => null} searchLabel="">
      <Grid container item xs={12}>
        <Grid item xs={12} mb={2}>
          <RestaurantHeader
            title="Discounts"
            onBackBtnClick={() => navigate(`/restaurant/${restaurantId}`)}
          />
        </Grid>

        <Grid item xs={12} sx={styles.mainView} px={3} pt={2} pb={3}>
          {isLoading ? (
            <Box sx={styles.loaderContainer}>
              <Loader />
            </Box>
          ) : (
            <>
              <Box sx={styles.headerRow} mb={2}>
                <Typography sx={styles.sectionTitle}>All Discounts</Typography>
                <Button
                  sx={styles.addBtn}
                  onClick={() => navigate(`/restaurant/${restaurantId}/discounts/new`)}
                >
                  + Add Discount
                </Button>
              </Box>

              <DiscountList
                discounts={discounts}
                onEdit={(discount) => navigate(`/restaurant/${restaurantId}/discounts/${discount._id}`)}
                onDelete={handleDeleteClick}
                onToggleStatus={handleToggleStatus}
              />

              {totalPages > 1 && discounts.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mt: 3,
                    width: '100%',
                  }}
                >
                  <Page
                    totalPages={totalPages}
                    pageNum={pageNum}
                    onSelectPage={(value: number) => setPageNum(value)}
                  />
                </Box>
              )}
            </>
          )}
        </Grid>
      </Grid>

      <AppDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        title="Disable Discount?"
        content={`Are you sure you want to disable "${discountToDelete?.description}"?`}
        firstBtnContent="Cancel"
        secondBtnContent="Disable"
        firstBtnClickHandler={handleDeleteCancel}
        secondBtnClickHandler={handleDeleteConfirm}
        options={{ keepMounted: false, btnLoading: deleteMutation.isPending }}
      />

      <AlertMsg
        msgAlert={msgAlert}
        onCloseAlertMsg={() => setMsgAlert({ ...msgAlert, open: false })}
      />
    </Container>
  );
};

export default DiscountsPage;

const styles = {
  mainView: {
    backgroundColor: '#FFF',
    borderRadius: '8px',
    minHeight: 'calc(100vh - 200px)',
  },
  loaderContainer: {
    width: '100%',
    minHeight: 'calc(100vh - 300px)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 600,
    fontFamily: 'GilroyBold',
    color: '#000',
  },
  addBtn: {
    backgroundColor: '#000',
    color: '#FFF',
    fontSize: 14,
    borderRadius: '8px',
    textTransform: 'capitalize' as const,
    paddingLeft: '1.5rem',
    paddingRight: '1.5rem',
    paddingTop: '8px',
    paddingBottom: '8px',
    '&:hover': {
      backgroundColor: '#333',
    },
  },
};
